import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, TextField, Button, Grid, Chip,
  Avatar, IconButton, MenuItem, Select, FormControl, InputLabel,
  Stepper, Step, StepLabel, Alert, CircularProgress, Autocomplete
} from '@mui/material';
import {
  PhotoCamera, Add as AddIcon
} from '@mui/icons-material';
import { useAuth } from '../../store/authStore';
import { updateTeacherProfile } from '../../api/marketplace';
import { getCurrentLanguage } from '../../utils/navigation';

const LANGUAGES = [
  'Arabic', 'English', 'French', 'Spanish', 'German', 'Italian',
  'Portuguese', 'Chinese', 'Japanese', 'Korean', 'Turkish', 'Russian',
  'Hindi', 'Urdu', 'Persian', 'Dutch', 'Swedish', 'Polish'
];

const SPECIALTIES = [
  'Conversational', 'Business', 'Test Preparation', 'Grammar',
  'Pronunciation', 'Writing', 'Reading', 'Listening', 'Kids',
  'Academic', 'Travel', 'Culture'
];

const COUNTRIES = [
  'Morocco', 'Algeria', 'Tunisia', 'Egypt', 'Saudi Arabia', 'UAE',
  'Jordan', 'Lebanon', 'Iraq', 'Syria', 'Palestine', 'Libya',
  'Sudan', 'Yemen', 'Oman', 'Qatar', 'Bahrain', 'Kuwait',
  'Turkey', 'France', 'Spain', 'Germany', 'UK', 'USA', 'Canada'
];

const steps = ['Personal Info', 'Teaching Profile', 'Pricing & Availability'];

const TeacherRegistration = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const lang = getCurrentLanguage();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(user?.photoURL || '');

  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    country: user?.country || '',
    bio: user?.bio || '',
    description: user?.description || '',
    languages: user?.languages || [],
    nativeLanguages: user?.nativeLanguages || [],
    specialties: user?.specialties || [],
    experience: user?.experience || '',
    education: user?.education || '',
    certifications: user?.certifications || [],
    pricePerHour: user?.pricePerHour || 15,
    trialPrice: user?.trialPrice || 5,
    lessonDuration: user?.lessonDuration || 50,
    currency: user?.currency || 'USD',
    newCertification: ''
  });

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const addCertification = () => {
    if (formData.newCertification.trim()) {
      setFormData(prev => ({
        ...prev,
        certifications: [...prev.certifications, prev.newCertification.trim()],
        newCertification: ''
      }));
    }
  };

  const removeCertification = (index) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!formData.firstName || !formData.lastName || !formData.country) {
        setError('Please fill in all required fields');
        return;
      }
    }
    if (activeStep === 1) {
      if (formData.languages.length === 0 || !formData.bio) {
        setError('Please add at least one language and a bio');
        return;
      }
    }
    setError('');
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      let photoURL = user?.photoURL;
      if (photoFile) {
        const reader = new FileReader();
        photoURL = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(photoFile);
        });
      }

      const profileData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        displayName: `${formData.firstName} ${formData.lastName}`,
        country: formData.country,
        bio: formData.bio,
        description: formData.description,
        languages: formData.languages,
        nativeLanguages: formData.nativeLanguages,
        specialties: formData.specialties,
        experience: formData.experience,
        education: formData.education,
        certifications: formData.certifications,
        pricePerHour: Number(formData.pricePerHour),
        trialPrice: Number(formData.trialPrice),
        lessonDuration: Number(formData.lessonDuration),
        currency: formData.currency,
        profileCompleted: true,
        profileApproved: false, // Admin needs to approve
        isNewUser: false
      };
      if (photoURL) profileData.photoURL = photoURL;

      await updateTeacherProfile(user.id, profileData);
      await updateUser({ ...user, ...profileData });
      navigate(`/${lang}/teacher/dashboard`);
    } catch (err) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const renderStep0 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={photoPreview}
            sx={{ width: 120, height: 120, fontSize: 48 }}
          >
            {formData.firstName?.charAt(0) || 'T'}
          </Avatar>
          <IconButton
            component="label"
            sx={{
              position: 'absolute', bottom: 0, right: 0,
              bgcolor: 'primary.main', color: 'white',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            <PhotoCamera fontSize="small" />
            <input type="file" hidden accept="image/*" onChange={handlePhotoChange} />
          </IconButton>
        </Box>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth label="First Name" required
          value={formData.firstName} onChange={handleChange('firstName')}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth label="Last Name" required
          value={formData.lastName} onChange={handleChange('lastName')}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth required>
          <InputLabel>Country</InputLabel>
          <Select value={formData.country} onChange={handleChange('country')} label="Country">
            {COUNTRIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth label="Short Bio" required
          multiline rows={2} placeholder="Brief introduction about yourself..."
          value={formData.bio} onChange={handleChange('bio')}
          helperText={`${formData.bio.length}/200 characters`}
          inputProps={{ maxLength: 200 }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth label="Detailed Description"
          multiline rows={4} placeholder="Tell students about your teaching style, experience, and what makes you unique..."
          value={formData.description} onChange={handleChange('description')}
        />
      </Grid>
    </Grid>
  );

  const renderStep1 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Autocomplete
          multiple value={formData.languages}
          onChange={(_, v) => setFormData(prev => ({ ...prev, languages: v }))}
          options={LANGUAGES} renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option} {...getTagProps({ index })} color="primary" variant="outlined" />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="Languages You Teach *" placeholder="Add languages" />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          multiple value={formData.nativeLanguages}
          onChange={(_, v) => setFormData(prev => ({ ...prev, nativeLanguages: v }))}
          options={LANGUAGES} renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option} {...getTagProps({ index })} color="success" variant="outlined" />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="Native Languages" placeholder="Add native languages" />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          multiple value={formData.specialties}
          onChange={(_, v) => setFormData(prev => ({ ...prev, specialties: v }))}
          options={SPECIALTIES} renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option} {...getTagProps({ index })} color="secondary" variant="outlined" />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="Specialties" placeholder="Add specialties" />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth label="Teaching Experience"
          multiline rows={3} placeholder="Describe your teaching experience..."
          value={formData.experience} onChange={handleChange('experience')}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth label="Education"
          multiline rows={2} placeholder="Your educational background..."
          value={formData.education} onChange={handleChange('education')}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>Certifications</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            size="small" fullWidth placeholder="Add a certification..."
            value={formData.newCertification} onChange={handleChange('newCertification')}
            onKeyPress={(e) => e.key === 'Enter' && addCertification()}
          />
          <Button variant="outlined" onClick={addCertification}><AddIcon /></Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {formData.certifications.map((cert, i) => (
            <Chip
              key={i} label={cert} onDelete={() => removeCertification(i)}
              variant="outlined"
            />
          ))}
        </Box>
      </Grid>
    </Grid>
  );

  const renderStep2 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth label="Price per Hour ($)" type="number" required
          value={formData.pricePerHour} onChange={handleChange('pricePerHour')}
          inputProps={{ min: 1, max: 500 }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth label="Trial Lesson Price ($)" type="number"
          value={formData.trialPrice} onChange={handleChange('trialPrice')}
          inputProps={{ min: 0, max: 100 }}
          helperText="Set to 0 for free trial"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Lesson Duration</InputLabel>
          <Select value={formData.lessonDuration} onChange={handleChange('lessonDuration')} label="Lesson Duration">
            <MenuItem value={25}>25 minutes</MenuItem>
            <MenuItem value={50}>50 minutes</MenuItem>
            <MenuItem value={75}>75 minutes</MenuItem>
            <MenuItem value={90}>90 minutes</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Currency</InputLabel>
          <Select value={formData.currency} onChange={handleChange('currency')} label="Currency">
            <MenuItem value="USD">USD ($)</MenuItem>
            <MenuItem value="EUR">EUR (€)</MenuItem>
            <MenuItem value="MAD">MAD (د.م.)</MenuItem>
            <MenuItem value="GBP">GBP (£)</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Alert severity="info">
          After saving, your profile will be reviewed by an admin before becoming visible to students.
          You can set your weekly availability schedule from the dashboard once approved.
        </Alert>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', pt: 10, pb: 4 }}>
      <Container maxWidth="md">
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom textAlign="center">
            Complete Your Teacher Profile
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" mb={4}>
            Fill in your details to start teaching on our platform
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map(label => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          {activeStep === 0 && renderStep0()}
          {activeStep === 1 && renderStep1()}
          {activeStep === 2 && renderStep2()}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
            >
              Back
            </Button>
            {activeStep < steps.length - 1 ? (
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button
                variant="contained" onClick={handleSubmit}
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {loading ? 'Saving...' : 'Save & Submit Profile'}
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TeacherRegistration;
